import { useTranslation } from 'react-i18next';
import { Package } from 'lucide-react';
import { RadioGroup } from '@/components/ui/radio-group';
import { ServiceListRow } from '@/components/booking/ServiceListRow';
import { ServiceCardSkeleton } from '@/components/booking/SkeletonLoader';
import { MarkdownDescription } from '@/components/MarkdownDescription';
import type { Service } from '@/types/booking';

interface ServiceSelectionProps {
  services: Service[];
  selectedService: Service | null;
  onSelectService: (service: Service) => void;
  isLoadingServices: boolean;
  formatCurrency: (amount: number) => string;
}

export function ServiceSelection({
  services,
  selectedService,
  onSelectService,
  isLoadingServices,
  formatCurrency,
}: ServiceSelectionProps) {
  const { t } = useTranslation(['publicBooking', 'common']);

  if (isLoadingServices) {
    return (
      <div className="space-y-4">
        <p className="text-body-sm text-soft">{t('publicBooking:service.description')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ServiceCardSkeleton />
          <ServiceCardSkeleton />
          <ServiceCardSkeleton />
          <ServiceCardSkeleton />
        </div>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-body-sm text-soft">{t('publicBooking:service.description')}</p>
        <div className="flex flex-col items-center py-8 text-center">
          <Package className="h-12 w-12 text-soft mb-4" />
          <p className="text-soft">{t('publicBooking:service.noServices')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-body-sm text-soft">{t('publicBooking:service.description')}</p>
      <RadioGroup
        value={selectedService ? selectedService.id.toString() : undefined}
        onValueChange={(value) => {
          const svc = services.find((s) => s.id.toString() === value);
          if (svc) onSelectService(svc);
        }}
        className="gap-3"
      >
        {services.map((service) => {
          const duration = service.duration || (service as { duration_minutes?: number }).duration_minutes;
          return (
            <ServiceListRow
              key={service.id}
              value={service.id.toString()}
              name={service.name}
              description={
                service.description ? (
                  <MarkdownDescription content={service.description} className="text-body-sm text-soft" />
                ) : undefined
              }
              duration={duration}
              price={
                !service.hide_price ? formatCurrency(service.price || 0) : undefined
              }
              data-testid={`service-${service.id}`}
            />
          );
        })}
      </RadioGroup>
    </div>
  );
}
